import { useCallback } from 'react'
import { Modal, message } from 'antd'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import { ContentType } from 'common/src/commentThread/types'
import {
    ANONYMOUS_DISPLAY_NAME,
    JEPNotificationType,
} from 'common/src/api/websiteFrontendVsWebsiteBackend/notifications/types'
import {
    ContentCreatorCommentNotification,
    ContentCreatorNotification,
} from 'common/src/api/contentFrontendVsContentBackend/types'
import { UserType } from 'magic-text-box-lexical/src/context/AnnotatedContentContext/AnnotatedContent.types'
import {
    batchToggleNotificationComplete,
    getNotificationQueue,
    toggleNotificationComplete,
} from '@/api/cb/notification'
import {
    notificationQueueAtom,
    notificationQueueFilterAtom,
} from '@/atoms/notifications'

type QueuedNotification = ContentCreatorNotification & { id: string }
type QueuedCommentNotification = ContentCreatorCommentNotification & {
    id: string
}

// A comment notification carries no comment id — only the thread (content
// type + id), the commenter's display name, and a timestamp stamped when WB
// relayed the comment to CB, seconds after the comment itself. That's what
// we match on. The slack covers clock skew and slow relays.
const MATCH_WINDOW_MS = 10 * 60 * 1000

// Enough to cover a course's whole open queue in one read.
const QUEUE_READ_SIZE = 500

export interface RepliedCommentInfo {
    displayName: string
    isAnonymous: boolean
    createdAt: Date | string
}

export interface ClearCommentNotificationPromptArgs {
    courseName: string
    contentType: ContentType
    contentId: string
    // The student comment that was replied to. Omit for a plain post to the
    // thread, which offers every open student-comment notification on it.
    comment?: RepliedCommentInfo
}

const isCommentNotification = (
    notification: QueuedNotification
): notification is QueuedCommentNotification =>
    notification.type === JEPNotificationType.COMMENT

const pickNotificationForComment = (
    candidates: QueuedCommentNotification[],
    comment: RepliedCommentInfo
): QueuedCommentNotification[] => {
    const commentTime = new Date(comment.createdAt).getTime()
    const expectedName = comment.isAnonymous
        ? ANONYMOUS_DISPLAY_NAME
        : comment.displayName
    const withinWindow = candidates
        .map((notification) => ({
            notification,
            gap: Math.abs(
                new Date(notification.createdAt).getTime() - commentTime
            ),
        }))
        .filter(({ gap }) => gap <= MATCH_WINDOW_MS)
    const byName = withinWindow.filter(
        ({ notification }) =>
            notification.metadata.commentUserDisplayName === expectedName
    )
    const pool = byName.length ? byName : withinWindow
    if (!pool.length) return []
    pool.sort((a, b) => a.gap - b.gap)
    return [pool[0].notification]
}

// After an instructor replies to a student comment, offer to clear that
// comment's row from the notification queue (AJ, 2026-08-27) — the reply is
// the work the queue row was asking for, so finishing it there saves a trip
// back to the queue to tick the box.
export const useClearCommentNotificationPrompt = (): ((
    args: ClearCommentNotificationPromptArgs
) => Promise<void>) => {
    const setQueueData = useSetRecoilState(notificationQueueAtom)
    const queueFilter = useRecoilValue(notificationQueueFilterAtom)

    const markComplete = useCallback(
        async (courseName: string, ids: string[]): Promise<void> => {
            try {
                await batchToggleNotificationComplete(courseName, ids, true)
            } catch {
                // Same fallback the queue modal uses when batch is unavailable.
                await Promise.all(
                    ids.map((id) =>
                        toggleNotificationComplete(courseName, id, true)
                    )
                )
            }
            // Keep an open queue modal in step without a refetch.
            setQueueData((prev) => {
                if (!prev) return prev
                const idSet = new Set(ids)
                if (queueFilter === 'incomplete') {
                    const remaining = prev.notifications.filter(
                        (n) => !idSet.has(n.id)
                    )
                    return {
                        ...prev,
                        notifications: remaining,
                        totalCount:
                            prev.totalCount -
                            (prev.notifications.length - remaining.length),
                    }
                }
                return {
                    ...prev,
                    notifications: prev.notifications.map((n) =>
                        idSet.has(n.id) ? { ...n, isCompleted: true } : n
                    ),
                }
            })
        },
        [queueFilter, setQueueData]
    )

    return useCallback(
        async (args: ClearCommentNotificationPromptArgs): Promise<void> => {
            let open: QueuedNotification[]
            try {
                const res = await getNotificationQueue(
                    args.courseName,
                    1,
                    QUEUE_READ_SIZE,
                    'incomplete',
                    'newest'
                )
                if (!res.data.success) return
                open = res.data.payload.notifications
            } catch {
                return
            }

            const onThread = open
                .filter(isCommentNotification)
                .filter(
                    (n) =>
                        n.metadata.contentType === args.contentType &&
                        n.metadata.contentId === args.contentId
                )
            const matches = args.comment
                ? pickNotificationForComment(onThread, args.comment)
                : onThread.filter(
                      (n) => n.metadata.commentUserType === UserType.student
                  )
            if (!matches.length) return

            const ids = matches.map((n) => n.id)
            const subject = args.comment
                ? args.comment.isAnonymous
                    ? 'this comment'
                    : `${args.comment.displayName}'s comment`
                : 'this thread'
            const body =
                ids.length === 1
                    ? `Mark the notification for ${subject} as complete?`
                    : `Mark the ${ids.length} open notifications on ${subject} as complete?`

            Modal.confirm({
                title: 'Clear that notification from the queue?',
                content: body,
                okText: 'Clear',
                cancelText: 'Keep',
                onOk: async () => {
                    try {
                        await markComplete(args.courseName, ids)
                        message.success(
                            ids.length === 1
                                ? 'Notification cleared'
                                : `${ids.length} notifications cleared`
                        )
                    } catch {
                        message.error('Could not clear the notification.')
                    }
                },
            })
        },
        [markComplete]
    )
}
